import { UserServiceContext } from '@goparrot-dashboard-core/user-service';
import { hasSomePermission } from '@goparrot/users-v2-sdk';
import * as React from 'react';
import { Route } from 'react-router-dom';
import { getPageNameByPath, checkForPermissions } from '@goparrot-dashboard/app';
import { useSquareNav, InsufficientPermissions as SquareInsufficientPermissions } from '@goparrot-dashboard/shared-utils';
import { RouteProps } from 'react-router';
import { PermissionsV2ValueType } from '@goparrot/users-v2-sdk/types/role-v2/permission';
import { ErrorBoundary } from 'components/commons/ErrorBoundary';
import { InsufficientPermissions } from 'components/commons/utility';

type PermissionType = PermissionsV2ValueType;

type Props = RouteProps & {
  allowed?: Array<PermissionsV2ValueType | PermissionsV2ValueType[]>;
  restricted?: PermissionType[];
  disabled?: boolean;
};

export const RoleRoute: React.FC<React.PropsWithChildren<Props>> = ({ allowed, restricted, disabled, ...props }) => {
  const isSquareNavEnabled = useSquareNav();
  const homePath = isSquareNavEnabled ? '/home' : '/dashboard';

  if (disabled) {
    return null;
  }

  if (!allowed) {
    return (
      <UserServiceContext.Consumer>
        {({ user }) => (
          <ErrorBoundary user={user}>
            <Route {...props} />
          </ErrorBoundary>
        )}
      </UserServiceContext.Consumer>
    );
  }

  return (
    <UserServiceContext.Consumer>
      {({ roleV2, roleV2QueryResult, user }) => {
        if (roleV2QueryResult.isLoading) {
          return null;
        }
        if (!roleV2) {
          return isSquareNavEnabled ? <SquareInsufficientPermissions pageName={getPageNameByPath(props.path as string)} /> : <InsufficientPermissions />;
        }
        const isRestricted = !!restricted?.length && hasSomePermission(roleV2, restricted);
        // TODO: find duplicates of the line below across the app and move to a utility function
        const hasAccess = allowed.some((permissionGroup) => checkForPermissions(permissionGroup, roleV2));

        // don't show insufficient permissions page on home. NavBar component will do the redirect
        if (props.path === homePath && !hasAccess) {
          return null;
        }

        if (hasAccess && !isRestricted) {
          return (
            <ErrorBoundary user={user}>
              <Route {...props} />
            </ErrorBoundary>
          );
        }
        return isSquareNavEnabled ? <SquareInsufficientPermissions pageName={getPageNameByPath(props.path as string)} /> : <InsufficientPermissions />;
      }}
    </UserServiceContext.Consumer>
  );
};
